import classNames from 'classnames';
import React from 'react';
import { InputNumber, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import './index.less';
import { EDeformationImgType } from '@meitu/saas-editor-core/es/plugins/deformation-img/states/deformation-img';

interface IProps {
    id: string;
    rotate: number;
    lock: boolean;
    disableFlipBtn?: boolean;
    onRotate?: () => void;
    onFlip?: (flip: 'horizontal' | 'vertical') => void;
    onDeformationImg?: (type: EDeformationImgType) => void;
}

const AdjustInspector: React.FC<IProps> = (props) => {
    const { id, rotate, lock, disableFlipBtn, onFlip, onRotate, onDeformationImg } = props;

    const { $t } = useLocaleStore();
    const editor = useEditor();

    const __disableFlipBtn = disableFlipBtn || lock;
    const setRotate = (val: number | null, options?: { writeHistory: boolean }) => {
        if (val === null) return;
        const rotate = ((val % 360) + 360) % 360;
        editor.layer.modifyBasicProperties.rotate(id, rotate, options);
    };

    const getActualAngle = (angle: number) => {
        let newRotate = angle % 360;
        if (newRotate < 0) {
            newRotate += 360;
        }
        return ~~(newRotate <= 180 ? newRotate : -(360 - newRotate));
    };

    return (
        <div className="c-base-toolbar-controller c-base-toolbar-controller-vertical">
            <LabelAction title={$t((v) => v.baseToolbar.adjust)} align vertical>
                <div className="c-base-toolbar-controller-content">
                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.perspective)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: lock,
                            })}
                            onClick={() => {
                                if (lock) return;
                                onDeformationImg?.(EDeformationImgType.PERSPECTIVE);
                            }}
                        >
                            <IconFont name="Deformation" fontSize={14} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.deformation)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: lock,
                            })}
                            onClick={() => {
                                if (lock) return;
                                onDeformationImg?.(EDeformationImgType.DISTORTION);
                            }}
                        >
                            <IconFont name="Perspective" fontSize={14} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.flipHorizontal)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: __disableFlipBtn,
                            })}
                            onClick={() => {
                                if (__disableFlipBtn) return;
                                editor.layer.flipX(id);
                                onFlip?.('horizontal');
                            }}
                        >
                            <IconFont name="FlipHorizontalBold" fontSize={14} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.flipVertical)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: __disableFlipBtn,
                            })}
                            onClick={() => {
                                if (__disableFlipBtn) return;
                                editor.layer.flipY(id);
                                onFlip?.('vertical');
                            }}
                        >
                            <IconFont name="FlipVerticalBold" fontSize={14} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.leftRotate)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: lock,
                            })}
                            onClick={() => {
                                if (lock) return;
                                setRotate(rotate - 90, { writeHistory: true });
                                onRotate?.();
                            }}
                        >
                            <IconFont name="ArrowCounterclockwiseBold" fontSize={14}></IconFont>
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="bottom"
                        title={$t((v) => v.baseToolbar.rightRotate)}
                        getPopupContainer={(el) => el.parentNode as HTMLElement}
                    >
                        <div
                            className={classNames('c-base-toolbar-controller-item', {
                                disabled: lock,
                            })}
                            onClick={() => {
                                if (lock) return;
                                setRotate(rotate + 90, { writeHistory: true });
                                onRotate?.();
                            }}
                        >
                            <IconFont name="ArrowClockwiseBold" fontSize={14}></IconFont>
                        </div>
                    </Tooltip>
                </div>
            </LabelAction>
        </div>
    );
};

export default React.memo(AdjustInspector);
